import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Anvil, Coins, Lock, ChevronDown, ChevronUp, Sparkles, Check, X, Flame,
  Sword, Shield, ShieldCheck, ShieldOff, Shirt, HardHat, Link, Feather, Diamond, Shell,
  Snowflake, Zap, Bug, Eclipse, Hammer, Target, Skull, Swords, Hand, Circle,
  Gem, Eye, Footprints, Wallet, FlaskConical, Music, Crown, Ghost, Dice5, Leaf,
  Clock, Sun, CloudLightning, Compass, Axe, Anchor, Search
} from "lucide-react";
import { ALL_ITEMS, RARITY_COLORS, RARITY_ORDER, getItemById } from "@/config/items";

const ICON_MAP = {
  Sword, Shield, ShieldCheck, ShieldOff, Shirt, HardHat, Link, Feather, Diamond, Shell,
  Flame, Snowflake, Zap, Bug, Eclipse, Hammer, Target, Skull, Swords, Hand, Circle,
  Gem, Eye, Footprints, Wallet, FlaskConical, Music, Crown, Ghost, Dice5, Leaf,
  Clock, Sparkles, Sun, CloudLightning, Compass, Axe, Anchor, Search,
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fruitCrush_token';

// Material name lookup from items config
const MATERIAL_NAMES = {};
ALL_ITEMS.filter(i => i.subtype === 'material' || i.type === 'collectible').forEach(i => {
  MATERIAL_NAMES[i.id] = { name: i.name, emoji: i.emoji };
});

function MaterialBadge({ mat }) {
  const info = MATERIAL_NAMES[mat.material_id] || { name: mat.material_id.replace(/_/g, ' '), emoji: '' };
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${
      mat.sufficient ? 'bg-emerald-900/30 border border-emerald-500/20 text-emerald-300' : 'bg-red-900/20 border border-red-500/20 text-red-300'
    }`}>
      <span className="text-sm">{info.emoji}</span>
      <span className="capitalize">{info.name}</span>
      <span className={`font-bold ${mat.sufficient ? 'text-emerald-400' : 'text-red-400'}`}>
        {mat.owned}/{mat.needed}
      </span>
      {mat.sufficient ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3 text-red-400" />}
    </div>
  );
}

function RecipeCard({ recipe, onForge, forging }) {
  const [expanded, setExpanded] = useState(false);
  const rarity = RARITY_COLORS[recipe.rarity] || RARITY_COLORS.common;
  const itemData = getItemById(recipe.recipe_id);

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${
      recipe.unlocked
        ? `${rarity.bg} ${rarity.border} ${rarity.glow ? `shadow-lg ${rarity.glow}` : ''}`
        : 'bg-slate-800/20 border-slate-700/20 opacity-60'
    }`} data-testid={`recipe-${recipe.recipe_id}`}>
      {/* Header */}
      <button
        onClick={() => recipe.unlocked && setExpanded(!expanded)}
        className="w-full p-3 flex items-center gap-3 text-left"
      >
        {/* Item icon */}
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          recipe.unlocked ? `${rarity.bg} border ${rarity.border}` : 'bg-slate-800 border border-slate-700'
        }`}>
          {recipe.unlocked ? (
            (() => {
              const IconComp = itemData?.icon ? ICON_MAP[itemData.icon] : null;
              if (IconComp) return <IconComp className="w-5 h-5 text-white/80" />;
              if (itemData?.emoji) return <span className="text-lg">{itemData.emoji}</span>;
              return <Anvil className="w-5 h-5 text-white/60" />;
            })()
          ) : (
            <Lock className="w-4 h-4 text-slate-600" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className={`font-bold text-sm truncate ${recipe.unlocked ? 'text-white' : 'text-slate-600'}`}>
              {recipe.unlocked ? recipe.name : '???'}
            </h3>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${rarity.bg} ${rarity.text} border ${rarity.border}`}>
              {rarity.label}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-slate-400 capitalize">{recipe.category}</span>
            {!recipe.unlocked && (
              <span className="text-[10px] text-amber-400">Reach Lv.{recipe.min_level}</span>
            )}
            {recipe.times_crafted > 0 && (
              <span className="text-[10px] text-cyan-400">Crafted x{recipe.times_crafted}</span>
            )}
          </div>
        </div>

        {/* Status / Expand */}
        <div className="flex items-center gap-1.5 shrink-0">
          {recipe.can_craft && (
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          )}
          {recipe.unlocked && (
            expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expanded Detail */}
      {expanded && recipe.unlocked && (
        <div className="px-3 pb-3 space-y-2 border-t border-white/5 pt-2">
          {/* Description from items config */}
          {itemData?.description && (
            <p className="text-xs text-slate-300 leading-relaxed">{itemData.description}</p>
          )}

          {/* Materials */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Materials Required</p>
            <div className="flex flex-wrap gap-1.5">
              {recipe.materials.map(mat => (
                <MaterialBadge key={mat.material_id} mat={mat} />
              ))}
            </div>
          </div>

          {/* Cost + Forge */}
          <div className="flex items-center justify-between pt-1">
            <div className={`flex items-center gap-1 text-xs font-bold ${recipe.has_coins ? 'text-yellow-400' : 'text-red-400'}`}>
              <Coins className="w-3.5 h-3.5" />
              {recipe.coin_cost.toLocaleString()} coins
              {!recipe.has_coins && <span className="text-[10px] text-red-300 ml-1">(not enough)</span>}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onForge(recipe.recipe_id); }}
              disabled={!recipe.can_craft || forging}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                recipe.can_craft && !forging
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:brightness-110 active:scale-95 shadow-lg shadow-amber-500/20'
                  : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
              }`}
              data-testid={`forge-${recipe.recipe_id}`}
            >
              <Anvil className="w-3.5 h-3.5" />
              {forging ? 'Forging...' : 'Forge'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CraftingPage() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [playerCoins, setPlayerCoins] = useState(0);
  const [playerLevel, setPlayerLevel] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forging, setForging] = useState(false);
  const [forgeResult, setForgeResult] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [rarityFilter, setRarityFilter] = useState('all');

  const fetchRecipes = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/crafting/recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecipes(data.recipes || []);
        setPlayerCoins(data.player_coins || 0);
        setPlayerLevel(data.player_level || 0);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchRecipes(); }, []);

  const handleForge = async (recipeId) => {
    setForging(true);
    setForgeResult(null);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API_URL}/api/crafting/forge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipe_id: recipeId })
      });
      const data = await res.json();
      if (res.ok) {
        setForgeResult({ success: true, item: data.crafted_item });
        fetchRecipes(); // refresh
        setTimeout(() => setForgeResult(null), 4000);
      } else {
        setForgeResult({ success: false, message: data.detail });
        setTimeout(() => setForgeResult(null), 3000);
      }
    } catch (e) {
      setForgeResult({ success: false, message: 'Network error' });
      setTimeout(() => setForgeResult(null), 3000);
    }
    setForging(false);
  };

  const filtered = useMemo(() => {
    return recipes.filter(r => {
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
      if (rarityFilter !== 'all' && r.rarity !== rarityFilter) return false;
      return true;
    });
  }, [recipes, categoryFilter, rarityFilter]);

  const craftableCount = recipes.filter(r => r.can_craft).length;

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'weapon', label: 'Weapons' },
    { id: 'armor', label: 'Armor' },
    { id: 'accessory', label: 'Accessories' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Anvil className="w-12 h-12 text-amber-400 animate-bounce" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Forge success animation */}
      {forgeResult && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-2xl font-bold text-sm flex items-center gap-2 ${
          forgeResult.success
            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
            : 'bg-red-500 text-white'
        }`} style={{ animation: 'forgePopIn 0.4s ease-out' }}>
          {forgeResult.success ? (
            <>
              <Sparkles className="w-4 h-4" />
              Forged {forgeResult.item.name}!
              <Flame className="w-4 h-4" />
            </>
          ) : (
            <>{forgeResult.message}</>
          )}
        </div>
      )}

      <div className="container mx-auto px-3 py-4 max-w-2xl">
        {/* Header */}
        <header className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)} className="text-slate-300 flex items-center gap-1 text-sm hover:text-white transition-colors" data-testid="crafting-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="font-bold text-lg text-amber-300 flex items-center gap-1.5">
            <Anvil className="w-5 h-5" /> Forge
          </h1>
          <div className="flex items-center gap-1 text-xs text-yellow-400 font-bold">
            <Coins className="w-3.5 h-3.5" />
            {playerCoins.toLocaleString()}
          </div>
        </header>

        {/* Stats row */}
        <div className="flex items-center gap-3 mb-3 text-[11px]">
          <span className="text-slate-400">Level {playerLevel}</span>
          <span className="text-slate-600">|</span>
          <span className="text-slate-400">{recipes.length} recipes</span>
          {craftableCount > 0 && (
            <>
              <span className="text-slate-600">|</span>
              <span className="text-emerald-400 font-bold">{craftableCount} ready to forge</span>
            </>
          )}
        </div>

        {/* Category Tabs */}
        <div className="flex gap-1 mb-3" data-testid="crafting-categories">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                categoryFilter === cat.id
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  : 'bg-slate-800/40 text-slate-400 border border-transparent hover:bg-slate-800/60'
              }`}
            >
              {cat.label}
            </button>
          ))}
          <select
            value={rarityFilter}
            onChange={e => setRarityFilter(e.target.value)}
            className="ml-auto px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-white focus:outline-none"
            data-testid="crafting-rarity-filter"
          >
            <option value="all">All Rarity</option>
            {RARITY_ORDER.map(r => (
              <option key={r} value={r}>{RARITY_COLORS[r].label}</option>
            ))}
          </select>
        </div>

        {/* Recipe List */}
        <div className="space-y-2" data-testid="recipe-list">
          {filtered.map(recipe => (
            <RecipeCard key={recipe.recipe_id} recipe={recipe} onForge={handleForge} forging={forging} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Anvil className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No recipes match your filters.</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes forgePopIn {
          0% { opacity: 0; transform: translate(-50%, -20px) scale(0.9); }
          100% { opacity: 1; transform: translate(-50%, 0) scale(1); }
        }
      `}</style>
    </div>
  );
}
